import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Search, RotateCcw, Play } from 'lucide-react';
import { API_URLS, GCS_BUCKET_URL } from '../config/api';
import { apiGet } from '../utils/apiUtils';
import { t } from '../i18n';
import ParticleBackground from './ParticleBackground';
import SeoHead from './SeoHead';
import './LibraryPage.css';

const PER_PAGE = 6;

const LibraryPage = () => {
  const [nameFilter, setNameFilter] = useState('');
  const [novels, setNovels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalNovels, setTotalNovels] = useState(0);

  const fetchNovels = async (page = 1, name = nameFilter, suppressToast = false) => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * PER_PAGE;
      const url = API_URLS.LIBRARY_NOVELS(PER_PAGE, offset, name);
      const response = await apiGet(url, {}, false, '', suppressToast);
      setNovels(response.data.novel_briefs || []);
      setTotalNovels(response.data.max_novels || 0);
      setCurrentPage(page);
    } catch (err) {
      setError(t('Ошибка при загрузке библиотеки'));
      setNovels([]);
      setTotalNovels(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchNovels(1, nameFilter);
  };

  const handleReset = () => {
    setNameFilter('');
    fetchNovels(1, '');
  };

  useEffect(() => { fetchNovels(1, '', true); }, []);

  const totalPages = Math.ceil(totalNovels / PER_PAGE);

  return (
    <div className="lp-wrap">
      <SeoHead
        title={t('Моя библиотека')}
        description={t('Библиотека ваших интерактивных новелл. Возобновляйте прохождение, управляйте сохранениями и следите за историей своих игр.')}
        noindex
      />
      <ParticleBackground />

      <div className="lp-app">

        <div className="lp-header">
          <h1 className="lp-title">{t('Моя библиотека')}</h1>
        </div>

        <form className="lp-filters" onSubmit={handleSearch}>
          <div>
            <label className="lp-label">{t('Название')}</label>
            <input
              className="lp-input"
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder={t('Поиск по названию…')}
            />
          </div>
          <div className="lp-actions">
            <button type="submit" className="lp-btn-search">
              <Search size={14} /> {t('Поиск')}
            </button>
            <button type="button" className="lp-btn-reset" onClick={handleReset}>
              <RotateCcw size={14} /> {t('Сбросить')}
            </button>
          </div>
        </form>

        {loading ? (
          <div className="lp-loading">
            <div className="lp-spinner" />
            <span>{t('Загрузка…')}</span>
          </div>
        ) : error ? (
          <div className="lp-error">{error}</div>
        ) : novels.length === 0 ? (
          <div className="lp-empty">{t('В вашей библиотеке пока нет новелл')}</div>
        ) : (
          <>
            <div className="lp-grid">
              {novels.map((novel) => {
                const cover = novel.cover_background_image_id && novel.cover_background_image_extension
                  ? `${GCS_BUCKET_URL}/production/${novel.novel_id}/background/${novel.cover_background_image_id}.${novel.cover_background_image_extension}`
                  : '/default-cover.jpg';

                return (
                  <a className="lp-card" key={novel.novel_id} href={`/library/${novel.play_link}`}>
                    <div className="lp-cover">
                      <img src={cover} alt={novel.name} />
                    </div>
                    <div className="lp-card-body">
                      <span className="lp-name">{novel.name}</span>
                      <p className="lp-desc">{novel.description}</p>
                      <span className="lp-link-play">
                        <Play size={12} fill="currentColor" /> {t('Играть')}
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="lp-pagination">
                <button
                  className="lp-page-btn"
                  onClick={() => fetchNovels(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="lp-page-info">{currentPage} / {totalPages}</span>
                <button
                  className="lp-page-btn"
                  onClick={() => fetchNovels(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}

        <div className="lp-spacer" />
      </div>
    </div>
  );
};

export default LibraryPage;
